import { logAutoCompleteSuggestionAcceptance } from "@/components/FullPageEditor/components/menus/GhostTextMenu/utils";
import { useNotePageContextSelector } from "@/components/NotePage/hooks/NotePageContext";
import { useChatEditorCommunicator } from "@/features/AI/KaiChat/hooks/useChatEditorCommunicator";
import { useAutoSave } from "@/hooks/useAutoSave";
import { useCommandSave } from "@/hooks/useCommandSave";
import { report } from "@/utils/analytics/logging";
import { Editor, useEditor } from "@knowt/editor/react";
import { AICompletionType, ItemType } from "@knowt/syncing/graphql/schema";
import { NoteAutocompleteType } from "@knowt/syncing/hooks/ai/constants";
import { useAICopilot } from "@knowt/syncing/hooks/ai/useAICopilot";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import { useDebouncedCallback } from "@knowt/syncing/utils/hooks/useDebouncedCallback";
import { createContext, useContextSelector } from "@knowt/syncing/utils/use-context-selector";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import CustomPlaceholderUI from "../../NotePage/Document/CustomPlaceholderUI";
import { extensionKit } from "../extensions/extensionKit";
import { useRefMyFunc } from "./useRefMyFunc";
import { parseContent } from "../utils/contentParser";
import { getPasteTransformers } from "../utils/getPasteTransformers";
import { getNoteMainTitleText } from "../../NotePage/Document/Editor/NoteMainTitle/NoteMainTitle";

type EditorContextValue = {
    editor: Editor | null;
    manuallySave: () => Promise<void>;
    printDocument?: () => void;
    isSaving: boolean;
};

type EditorContextProps = {
    children: React.ReactNode;
    editable: boolean;
    ssr?: boolean;
    content: string;
    saveContent?: (content: string) => void | Promise<void>;
    allowAutoSave?: boolean;
    printDocument?: () => void;
    disabledCustomPlaceholder?: boolean;
    characterCountLimit?: number;
    placeholder?: string;
    onAITrigger?: () => void;
};

const EditorContext = createContext<EditorContextValue | null>(null);

export const EditorContextProvider = ({
    children,
    editable,
    ssr,
    content,
    saveContent,
    allowAutoSave = true,
    printDocument,
    disabledCustomPlaceholder,
    characterCountLimit,
    placeholder,
    onAITrigger,
}: EditorContextProps) => {
    const { user } = useCurrentUser();

    const note = useNotePageContextSelector(state => state?.note);
    const handleSelect = useChatEditorCommunicator(s => s?.handleSelect);

    const renderAITemplateRef = useRefMyFunc(({ editor }: { editor: Editor }) => {
        return <CustomPlaceholderUI editor={editor} />;
    });

    const { getSuggestion } = useAICopilot({
        itemType: ItemType.NOTE,
        itemId: note?.noteId as string,
    });

    const editor = useEditor(
        {
            content: parseContent(content),
            editorProps: {
                attributes: { autocomplete: "off", autocorrect: "off", autocapitalize: "off" },
                ...getPasteTransformers(),
            },
            onFocus: async ({ editor }) => {
                const text = editor.getText();
                if (!text) {
                    editor?.commands?.populateSuggestion?.({
                        alternate: false,
                    });
                }
            },
            immediatelyRender: !ssr,
            shouldRerenderOnTransaction: false,
            extensions: extensionKit({
                editable,
                characterCountLimit,
                onAITrigger: () => null,
                customPlaceholder: disabledCustomPlaceholder
                    ? undefined
                    : ({ editor }) => renderAITemplateRef?.current?.({ editor }),
                placeholderOptions: placeholder ? { placeholder } : undefined,
                copilotProps: {
                    timeout: note?.autocompleteSettings?.speed || user?.autocompleteSettings?.speed || 2000,
                    disabled: ({ editor }) => {
                        const isDisableBySettings = note?.autocompleteSettings
                            ? !!note?.autocompleteSettings?.disabled
                            : !!user?.autocompleteSettings?.disabled;

                        const noTitle = !getNoteMainTitleText();
                        const noContent = !editor?.getText();
                        const isDisableByBehavior = noTitle && noContent;

                        return isDisableBySettings || isDisableByBehavior;
                    },
                    fetchSuggestion: async ({ sentence, context }) => {
                        const noteTitle = getNoteMainTitleText();

                        if (!noteTitle && !context) return "";

                        const res = await getSuggestion({
                            type: AICompletionType.NOTE_AUTOCOMPLETE,
                            aiParams: {
                                type: NoteAutocompleteType.AUTOCOMPLETE,
                                sentence: sentence,
                                topic: noteTitle,
                                context: context,
                                messages: [],
                            },
                        });

                        return res;
                    },
                    fetchAlternateSuggestion: async ({ context, sentence }) => {
                        const res = await getSuggestion({
                            type: AICompletionType.NOTE_AUTOCOMPLETE,
                            aiParams: {
                                type: NoteAutocompleteType.REVISION,
                                sentence: sentence,
                                topic: getNoteMainTitleText(),
                                context: context,
                                messages: [],
                            },
                        });

                        return res;
                    },
                    onSuggestionAccept: ({ state, editor }) => {
                        logAutoCompleteSuggestionAcceptance({
                            isAccepted: state,
                        });

                        // generate a new suggestion once accepted
                        if (
                            state && note?.autocompleteSettings
                                ? !note?.autocompleteSettings?.disabled
                                : !user?.autocompleteSettings?.disabled
                        ) {
                            setTimeout(() => editor.commands.tryPopulateSuggestion({ alternate: false }));
                        }
                    },
                },
            }),
            onSelectionUpdate: handleSelect,
            editable,
        },
        []
    );

    // hack-way so far notice ai is triggered
    useEffect(() => {
        if (!editor) return;

        editor.on("aiTrigger", () => {
            onAITrigger?.();
        });

        return () => {
            editor.off("aiTrigger", onAITrigger);
        };
    }, [onAITrigger, editor]);

    useEffect(() => {
        const transactionHandler = ({ transaction }) => {
            if (transaction.getMeta("characterCount")?.pastedContentSizeExceededLimit)
                toast.error("Pasted content too long!");
        };

        editor?.on("transaction", transactionHandler);
        return () => {
            editor?.off("transaction", transactionHandler);
        };
    }, [editor]);

    const save = useCallback(
        async (editor: Editor) => {
            setIsSaving(true);
            await saveContent?.(editor.getHTML());
            setIsSaving(false);
            setIsDirty(false);
        },
        [saveContent]
    );

    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const manuallySave = async () => {
        if (!saveContent) {
            report(new Error("saveContent must be defined to allow manual saving"), "manuallySave");
            return;
        }

        if (editor) await save(editor);
    };

    useCommandSave({
        successMsg: "Note saved!",
        errorMsg:
            "Failed to save the note. Please copy the content to somewhere safe, check your network connection, refresh the page, and try again.",
        save: manuallySave,
        disabled: !editable,
    });

    useAutoSave({
        isDirty: () => !!saveContent && !!allowAutoSave && isDirty,
        save: manuallySave,
        interval: 5_000,
        block: true,
    });

    const debouncedSave = useDebouncedCallback(async (editor: Editor) => save(editor), 10_000);

    useEffect(() => {
        const updateHandler = async ({ editor }) => {
            if (editor.isEditable) {
                await debouncedSave(editor);
                setIsDirty(true);
            }
        };

        editor?.on("update", updateHandler);
        return () => {
            editor?.off("update", updateHandler);
        };
    }, [debouncedSave, editor]);

    return (
        <EditorContext.Provider
            value={{
                editor,
                manuallySave,
                printDocument,
                isSaving,
            }}>
            {children}
        </EditorContext.Provider>
    );
};

export const useEditorContextSelector = <T,>(selector: (value: EditorContextValue) => T) =>
    useContextSelector(EditorContext, selector);
